<template>
  <header class="fixed w-full top-0 z-50 transition-all duration-300" :class="[
    isScrolled ? 'glass shadow-sm py-2' : 'bg-transparent py-4'
  ]">
    <div class="container-custom">
      <div class="flex items-center justify-between">
        <!-- Logo -->
        <router-link to="/" class="flex items-center group">
          <img src="@/assets/logo/logo_horizntal.png" alt="Ruxzom Logo"
            class="h-[45px] h-[40px] w-auto transition-transform duration-300 group-hover:scale-105 md:mt-2" />
        </router-link>

        <!-- Desktop Navigation -->
        <nav class="hidden md:flex items-center space-x-8">
          <router-link v-for="item in navigation" :key="item.name" :to="item.href"
            class="nav-link text-sm uppercase tracking-wide transition-colors duration-300"
            :class="[useBlackText ? 'text-gray-900 hover:text-primary-600' : 'text-white hover:text-blue-400']"
            active-class="router-link-active">
            {{ item.name }}
          </router-link>
        </nav>

        <!-- Auth Buttons / User Menu -->
        <div class="flex items-center space-x-4">
          <template v-if="!authStore.isAuthenticated">
            <router-link to="/login"
              class="hidden md:inline-block font-medium transition-colors duration-300"
              :class="[useBlackText ? 'text-gray-600 hover:text-primary-600' : 'text-white hover:text-blue-400']">
              Login
            </router-link>
            <router-link to="/register" class="btn btn-primary rounded-full px-6">
              Get Started
            </router-link>
          </template>

          <template v-else>
            <!-- User Dropdown -->
            <div class="relative" ref="userMenuRef">
              <button @click="showUserMenu = !showUserMenu"
                class="flex items-center space-x-3 focus:outline-none transition-colors duration-300"
                :class="[useBlackText ? 'text-gray-700 hover:text-primary-600' : 'text-white hover:text-blue-400']">
                <div
                  class="w-9 h-9 bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center border-2 border-white shadow-sm">
                  <span class="text-primary-700 font-bold text-sm">
                    {{ authStore.user?.name?.charAt(0) }}
                  </span>
                </div>
                <span class="hidden sm:block font-medium">{{ authStore.user?.name }}</span>
                <svg class="w-4 h-4 opacity-70" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </button>

              <!-- Dropdown Menu -->
              <transition enter-active-class="transition ease-out duration-200"
                enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95">
                <div v-show="showUserMenu"
                  class="absolute right-0 mt-3 w-56 bg-white rounded-xl shadow-xl py-2 z-50 border border-gray-100 ring-1 ring-black ring-opacity-5">
                  <div class="px-4 py-3 border-b border-gray-50">
                    <p class="text-sm text-gray-500">Signed in as</p>
                    <p class="text-sm font-medium text-gray-900 truncate">{{ authStore.user?.email }}</p>
                  </div>

                  <div class="py-1">
                    <router-link v-if="authStore.user?.role === 'admin'" to="/dashboard/admin"
                      class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                      @click="showUserMenu = false">
                      <svg class="mr-3 h-5 w-5 text-gray-400 group-hover:text-primary-500" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z">
                        </path>
                      </svg>
                      Admin Dashboard
                    </router-link>

                    <router-link v-if="authStore.user?.role === 'developer'" to="/dashboard/developer"
                      class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                      @click="showUserMenu = false">
                      <svg class="mr-3 h-5 w-5 text-gray-400 group-hover:text-primary-500" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z">
                        </path>
                      </svg>
                      Developer Dashboard
                    </router-link>

                    <router-link v-if="authStore.user?.role === 'client'" to="/dashboard/client"
                      class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                      @click="showUserMenu = false">
                      <svg class="mr-3 h-5 w-5 text-gray-400 group-hover:text-primary-500" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6">
                        </path>
                      </svg>
                      Dashboard
                    </router-link>
                    <router-link :to="authStore.user?.role === 'admin' ? '/dashboard/admin/profile' : (authStore.user?.role === 'developer' ? '/dashboard/developer/profile' : '/dashboard/client/profile')"
                      class="group flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-primary-50 hover:text-primary-600"
                      @click="showUserMenu = false">
                      <svg class="mr-3 h-5 w-5 text-gray-400 group-hover:text-primary-500" fill="none"
                        stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                      </svg>
                      Profile
                    </router-link>
                  </div>

                  <div class="border-t border-gray-50 py-1">
                    <button @click="handleLogout"
                      class="group flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-red-50 hover:text-red-600">
                      <svg class="mr-3 h-5 w-5 text-gray-400 group-hover:text-red-500" fill="none" stroke="currentColor"
                        viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1">
                        </path>
                      </svg>
                      Logout
                    </button>
                  </div>
                </div>
              </transition>
            </div>
          </template>

          <!-- Mobile Menu Button -->
          <button @click="showMobileMenu = !showMobileMenu"
            class="md:hidden p-2 rounded-lg text-gray-600 hover:text-primary-600 hover:bg-primary-50 focus:outline-none transition-colors duration-200">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path v-if="!showMobileMenu" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"></path>
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12">
              </path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Mobile Navigation -->
      <transition enter-active-class="transition ease-out duration-200"
        enter-from-class="transform opacity-0 -translate-y-2" enter-to-class="transform opacity-100 translate-y-0"
        leave-active-class="transition ease-in duration-150" leave-from-class="transform opacity-100 translate-y-0"
        leave-to-class="transform opacity-0 -translate-y-2">
        <div v-show="showMobileMenu"
          class="md:hidden py-4 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 px-4">
          <nav class="flex flex-col space-y-2">
            <router-link v-for="item in navigation" :key="item.name" :to="item.href"
              class="px-4 py-3 text-gray-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg font-medium transition-colors duration-200 flex items-center justify-between group"
              active-class="text-primary-600 bg-primary-50" @click="showMobileMenu = false">
              {{ item.name }}
              <svg class="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200" fill="none"
                stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
              </svg>
            </router-link>

            <template v-if="!authStore.isAuthenticated">
              <div class="border-t border-gray-100 my-2 pt-2 space-y-2">
                <router-link to="/login"
                  class="block w-full text-center px-4 py-2 text-gray-600 hover:text-primary-600 font-medium"
                  @click="showMobileMenu = false">
                  Login
                </router-link>
                <router-link to="/register" class="block w-full btn btn-primary text-center"
                  @click="showMobileMenu = false">
                  Get Started
                </router-link>
              </div>
            </template>
          </nav>
        </div>
      </transition>
    </div>
  </header>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const route = useRoute()
const authStore = useAuthStore()

// State
const showMobileMenu = ref(false)
const showUserMenu = ref(false)
const userMenuRef = ref(null)
const isScrolled = ref(false)

// Navigation items
const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Products', href: '/products' },
  { name: 'Services', href: '/services' },
  { name: 'Portfolio', href: '/portfolio' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' }
]

// Determine if the current page is one of the main menu items or home
const isMenuPage = computed(() => {
  return navigation.some(item => route.path === item.href)
})

// Combined color logic: Black on menu pages, White-to-Black on others
const useBlackText = computed(() => {
  return isScrolled.value || isMenuPage.value
})

// Methods
const handleLogout = () => {
  showUserMenu.value = false
  showMobileMenu.value = false
  authStore.logout()
}

// Close dropdowns when clicking outside
const handleClickOutside = (event) => {
  if (userMenuRef.value && !userMenuRef.value.contains(event.target)) {
    showUserMenu.value = false
  }
}

// Handle scroll effect
const handleScroll = () => {
  isScrolled.value = window.scrollY > 20
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('scroll', handleScroll)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('scroll', handleScroll)
})
</script>
